import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, User } from 'lucide-react';
import { useUserLevel } from '@/hooks/useUserLevel';

const UserLevel: React.FC = () => {
  const navigate = useNavigate();
  const { level, levels, updateLevel, isLoading } = useUserLevel();

  const handleLevelChange = async (newLevelId: string) => {
    try {
      await updateLevel(newLevelId);
    } catch (error) {
      console.error('Failed to update level:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">User Level</h1>
            <p className="text-muted-foreground">Manage your restriction level</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Current Level
            </CardTitle>
            <CardDescription>
              Your level determines the default restrictions applied to your device
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Select Level
              </label>
              <Select
                value={level?._id || ''}
                onValueChange={handleLevelChange}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a level..." />
                </SelectTrigger>
                <SelectContent>
                  {levels?.map((levelOption) => (
                    <SelectItem key={levelOption._id} value={levelOption._id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{levelOption.name}</span>
                        {levelOption.description && (
                          <span className="text-xs text-muted-foreground">
                            {levelOption.description}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {level && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium text-foreground mb-2">Current Level: {level.name}</h3>
                {level.description && (
                  <p className="text-sm text-muted-foreground">{level.description}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserLevel;